import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withPreloading, type PreloadingStrategy, type Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { provideHttpClient } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { provideMarkdown } from 'ngx-markdown';
import { provideHighcharts } from 'highcharts-angular';
import { appRoutes } from './app.routes';
import { AxiosClient } from '@gishub/infrastructure';
import { Configuration } from './shared/configuration/configuration';
import { TokenService } from './shared/services/token-service';
import { DarkModeProvider } from './shared/providers/dark-mode-provider';
import { HttpErrorHandlerService } from './services/http-error-handler-service';

// Precarga solo las rutas marcadas con data.preload (hoy, la raíz con el
// visor). Con el chunk de ArcGIS ya descargado, el login navega al instante.
class OptInPreloadStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return route.data?.['preload'] ? load() : of(null);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const config = inject(Configuration);
      const tokenService = inject(TokenService);
      const errorHandler = inject(HttpErrorHandlerService);
      AxiosClient.setApiUrl(config, () => tokenService.getAccessToken(), errorHandler);
    }),
    provideAppInitializer(() => {
      inject(DarkModeProvider);
    }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes, withComponentInputBinding(), withPreloading(OptInPreloadStrategy)),
    provideHttpClient(),
    MessageService,
    ConfirmationService,
    provideMarkdown(),
    provideHighcharts({
      instance: () => import('highcharts/esm/highcharts').then(m => m.default),
      modules: () => [
        import('highcharts/esm/modules/treemap'),
        import('highcharts/esm/modules/heatmap'),
        import('highcharts/esm/modules/sankey'),
        import('highcharts/esm/modules/exporting'),
      ],
    }),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        dateFormat: 'dd/mm/yy',
        today: 'Hoy',
        clear: 'Limpiar',
        weekHeader: 'Sm',
        chooseDate: 'Elegir fecha',
        chooseMonth: 'Elegir mes',
        chooseYear: 'Elegir año',
        prevMonth: 'Mes anterior',
        nextMonth: 'Mes siguiente',
        prevYear: 'Año anterior',
        nextYear: 'Año siguiente',
        prevDecade: 'Década anterior',
        nextDecade: 'Década siguiente',
      },
    }),
  ],
};

